/**
 * assets/product.js — 商品詳細ページ専用スクリプト
 *
 * 【処理の大まかな順序（init から呼ばれる順）】
 * 1. メディアギャラリー — メイン／サムネイルスライダーのループ表示
 * 2. のしオプション — 「のし必要」のとき詳細表示、「のし種類」で表書き入力の出し分け
 * 3. ギフトオプション — バリアントでギフト項目を隠す／のしをリセットなど
 * 4. 下部固定カートバー・オプションモーダル — 配置・開閉・必須チェック・数量同期
 * 5. 価格表示 — 本体価格の変化を固定バー／モーダルに反映、オプション付きの連動価格
 *
 * 依存: global.js の SliderComponent（ギャラリー）、テーマの subscribe / PUB_SUB_EVENTS（バリアント変更）
 */
(function () {
  var NAIRE_METHOD_FIELDS = [
    [],
    ['姓①'],
    ['名①', 'ふりがな'],
    ['姓①', '姓②'],
    ['姓①', '姓②', '姓③'],
    ['姓①', '名①', '姓②', '名②', '姓③', '名③'],
    ['姓①', '名①', '名②'],
    ['姓①', '名①', '名②', '名③'],
    ['法人名'],
    ['法人名', '姓①', '名①'],
    ['姓①', '名①'],
    ['姓①', '名①', '姓②', '名②'],
    ['法人名', '肩書', '姓①', '名①'],
  ];

  var NAIRE_CAPTION_METHODS = [3, 4, 5, 6, 7, 11];

  // ---------------------------------------------------------------------------
  // 1. メディアギャラリー：メイン／サムネイルスライダーを端まで行ったら反対側へループ
  //    サムネイルの矢印操作時は、対応するメイン画像も同時に切り替える
  // ---------------------------------------------------------------------------
  function enableLoopForGallerySliders() {
    const galleries = document.querySelectorAll('media-gallery');
    if (!galleries.length) return;

    galleries.forEach(function (gallery) {
      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');

      [viewer, thumbs].forEach(function (component) {
        if (!component || !component.slider || !component.prevButton || !component.nextButton) return;
        if (component.dataset.gallerySliderEnhanced === 'true') return;

        component.dataset.gallerySliderEnhanced = 'true';
        component.enableSliderLooping = true;
        component.prevButton.removeAttribute('disabled');
        component.nextButton.removeAttribute('disabled');

        var handleButtonClick = function (event) {
          event.preventDefault();
          event.stopImmediatePropagation();

          var items = component.sliderItemsToShow;
          if (!items || items.length < 2) return;

          var isNext = event.currentTarget.name === 'next';

          if (component === thumbs) {
            activateAdjacentThumbnail(gallery, component, isNext);
            return;
          }

          moveSliderWithLoop(component, isNext);
        };

        component.prevButton.addEventListener('click', handleButtonClick, true);
        component.nextButton.addEventListener('click', handleButtonClick, true);
      });
    });
  }

  function moveSliderWithLoop(component, isNext) {
    var items = component.sliderItemsToShow;
    var lastItem = items[items.length - 1];
    var targetPosition = isNext
      ? component.slider.scrollLeft + component.sliderItemOffset
      : component.slider.scrollLeft - component.sliderItemOffset;

    if (isNext && component.isSlideVisible(lastItem)) {
      targetPosition = 0;
    }

    if (!isNext && component.slider.scrollLeft <= 0) {
      targetPosition = lastItem.offsetLeft;
    }

    component.slider.scrollTo({ left: targetPosition, behavior: 'smooth' });
  }

  function activateAdjacentThumbnail(gallery, component, isNext) {
    var thumbnails = Array.from(component.sliderItemsToShow).filter(function (item) {
      return item.dataset && item.dataset.target;
    });
    if (!thumbnails.length || typeof gallery.setActiveMedia !== 'function') return;

    var activeThumbnail = component.querySelector('button[aria-current="true"]')?.closest('[data-target]');
    var currentIndex = thumbnails.indexOf(activeThumbnail);
    if (currentIndex < 0) currentIndex = 0;

    var nextIndex = isNext ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex >= thumbnails.length) nextIndex = 0;
    if (nextIndex < 0) nextIndex = thumbnails.length - 1;

    var nextThumbnail = thumbnails[nextIndex];
    gallery.setActiveMedia(nextThumbnail.dataset.target, false);
    component.slider.scrollTo({ left: nextThumbnail.offsetLeft, behavior: 'smooth' });
  }

  // ---------------------------------------------------------------------------
  // 2. のし（段階表示）：要不要 → 用途 → 種類 → 付け方 → 名入れ
  //    マークアップ: [data-noshi-option] / [data-noshi-detail] / [data-noshi-purpose] ほか
  // ---------------------------------------------------------------------------
  function setNoshiStepVisibility(el, show) {
    if (!el) return;
    if (show) {
      el.style.display = '';
      el.removeAttribute('hidden');
    } else {
      el.style.display = 'none';
      el.setAttribute('hidden', '');
    }
  }

  function isNoshiStepVisible(el) {
    if (!el) return false;
    if (el.hasAttribute('hidden')) return false;
    if (el.style.display === 'none') return false;
    return true;
  }

  function scrollOptionModalToElement(container, targetEl) {
    if (!isNoshiStepVisible(targetEl)) return;

    var modal = container.closest('.option-modal');
    var panel = modal ? modal.querySelector('.option-modal__panel') : null;

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (!isNoshiStepVisible(targetEl)) return;

        if (panel) {
          var panelRect = panel.getBoundingClientRect();
          var targetRect = targetEl.getBoundingClientRect();
          var scrollTop = panel.scrollTop + (targetRect.top - panelRect.top) - 12;
          panel.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
          return;
        }

        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function scrollToNoshiNextStep(container, step) {
    var target = null;

    if (step === 'required') {
      target = container.querySelector('[data-noshi-purpose]');
    } else if (step === 'purpose') {
      target = container.querySelector('[data-noshi-type]');
    } else if (step === 'type') {
      target = container.querySelector('[data-noshi-style]');
    } else if (step === 'style') {
      target = container.querySelector('[data-naire-method]');
    } else if (step === 'naire-method') {
      target = container.querySelector('[data-naire-inputs]');
    }

    scrollOptionModalToElement(container, target);
  }

  function getNoshiStepState(container) {
    var noshiYes = container.querySelector('input[name="properties[のし]"][value="のし必要"]');
    var isRequired = !!(noshiYes && noshiYes.checked);
    var purposeSelect = container.querySelector('select[name="properties[のし用途]"]');
    var hasPurpose = !!(purposeSelect && purposeSelect.value && purposeSelect.value !== '');
    var typeChecked = container.querySelector('input[name="properties[のし種類]"]:checked:not([disabled])');
    var hasType = !!(typeChecked && !typeChecked.hasAttribute('hidden'));
    var styleChecked = container.querySelector('input[name="properties[のしの付け方]"]:checked');
    var hasStyle = !!styleChecked;
    return {
      isRequired: isRequired,
      hasPurpose: hasPurpose,
      hasType: hasType,
      hasStyle: hasStyle,
    };
  }

  function resetNoshiTypeSelection(container) {
    container.querySelectorAll('input[name="properties[のし種類]"]').forEach(function (r) {
      r.checked = false;
    });
  }

  function setNoshiInputsEnabled(container, selector, enabled, clearText) {
    container.querySelectorAll(selector).forEach(function (inp) {
      inp.disabled = !enabled;
      if (!enabled && clearText && inp.type !== 'radio') inp.value = '';
    });
  }

  function updateOmotegaki(container, allowShow) {
    var omotegakiEl = container.querySelector('[data-noshi-omotegaki]');
    if (!omotegakiEl) return;
    var checkedType = container.querySelector('input[name="properties[のし種類]"]:checked:not([disabled])');
    var showOg =
      allowShow && !!(checkedType && !checkedType.hasAttribute('hidden') && checkedType.value.indexOf('自由入力') >= 0);
    setNoshiStepVisibility(omotegakiEl, showOg);
    var omotegakiInput = omotegakiEl.querySelector('input[name="properties[表書き]"]');
    if (omotegakiInput) {
      omotegakiInput.disabled = !showOg;
      if (!showOg) omotegakiInput.value = '';
    }
  }

  function updateNoshiTypeList(container) {
    var purposeSelect = container.querySelector('select[name="properties[のし用途]"]');
    var typeFieldset = container.querySelector('[data-noshi-type]');
    if (!purposeSelect || !typeFieldset) return;

    var purposeOpt = purposeSelect.selectedOptions && purposeSelect.selectedOptions[0];
    var purposeKey =
      purposeOpt && purposeOpt.dataset && purposeOpt.dataset.purpose ? String(purposeOpt.dataset.purpose) : '';

    var emptyEl = typeFieldset.querySelector('[data-noshi-type-empty]');
    var radios = typeFieldset.querySelectorAll('input[name="properties[のし種類]"]');
    var labels = typeFieldset.querySelectorAll('label[data-noshi-purpose]');

    if (!purposeKey) {
      radios.forEach(function (r) {
        r.checked = false;
        r.disabled = true;
        r.setAttribute('hidden', '');
      });
      labels.forEach(function (l) {
        l.setAttribute('hidden', '');
      });
      if (emptyEl) emptyEl.removeAttribute('hidden');
      return;
    }

    if (emptyEl) emptyEl.setAttribute('hidden', '');

    radios.forEach(function (r) {
      var match = String(r.dataset.noshiPurpose || '') === purposeKey;
      if (match) {
        r.removeAttribute('hidden');
        r.disabled = false;
      } else {
        r.checked = false;
        r.disabled = true;
        r.setAttribute('hidden', '');
      }
    });
    labels.forEach(function (l) {
      if (String(l.dataset.noshiPurpose || '') === purposeKey) {
        l.removeAttribute('hidden');
      } else {
        l.setAttribute('hidden', '');
      }
    });
  }

  /** のし詳細の段階表示を同期（用途 → 種類 → 付け方 → 名入れ） */
  function updateNoshiStepVisibility(container) {
    var purposeEl = container.querySelector('[data-noshi-purpose]');
    var typeEl = container.querySelector('[data-noshi-type]');
    var styleEl = container.querySelector('[data-noshi-style]');
    var naireMethodEl = container.querySelector('[data-naire-method]');
    var naireInputsEl = container.querySelector('[data-naire-inputs]');
    var state = getNoshiStepState(container);

    var showPurpose = state.isRequired;
    var showType = state.isRequired && state.hasPurpose;
    var showStyle = state.isRequired && state.hasType;
    var showNaire = state.isRequired && state.hasType && state.hasStyle;

    setNoshiStepVisibility(purposeEl, showPurpose);
    setNoshiStepVisibility(typeEl, showType);
    setNoshiStepVisibility(styleEl, showStyle);
    setNoshiStepVisibility(naireMethodEl, showNaire);
    setNoshiStepVisibility(naireInputsEl, showNaire);

    setNoshiInputsEnabled(container, '[data-noshi-purpose] select', showPurpose, true);
    setNoshiInputsEnabled(
      container,
      '[data-noshi-type] input[name="properties[のし種類]"]',
      showType,
      false
    );
    setNoshiInputsEnabled(
      container,
      '[data-noshi-style] input[name="properties[のしの付け方]"]',
      showStyle,
      false
    );
    setNoshiInputsEnabled(container, '[data-naire-method] input', showNaire, false);
    setNoshiInputsEnabled(container, '[data-naire-inputs] input', showNaire, true);

    if (!state.hasPurpose) {
      resetNoshiTypeSelection(container);
    }

    updateOmotegaki(container, showStyle);
    if (showNaire) {
      updateNaireFields(container);
    }
  }

  function updateNaireFields(container) {
    var naireWrap = container.querySelector('[data-naire-inputs]');
    if (!naireWrap) return;

    var methodRadio = container.querySelector('input[name="properties[名入れ方法]"]:checked');
    var methodIdx =
      methodRadio && methodRadio.dataset.method !== undefined ? parseInt(methodRadio.dataset.method, 10) : 0;
    if (isNaN(methodIdx)) methodIdx = 0;

    var fields = NAIRE_METHOD_FIELDS[methodIdx] || [];
    var showPrintBlock = methodIdx !== 0;

    var printHeading = naireWrap.querySelector('.naire-print-heading');
    if (printHeading) printHeading.style.display = showPrintBlock ? '' : 'none';

    naireWrap.querySelectorAll('.naire-field').forEach(function (row) {
      var inputs = row.querySelectorAll('input[data-field]');
      var anyShow = Array.prototype.some.call(inputs, function (inp) {
        return fields.indexOf(inp.getAttribute('data-field')) >= 0;
      });
      row.style.display = anyShow ? '' : 'none';
      if (anyShow) row.removeAttribute('hidden');
      else row.setAttribute('hidden', '');

      inputs.forEach(function (inp) {
        var fname = inp.getAttribute('data-field');
        var show = fields.indexOf(fname) >= 0;
        inp.disabled = !show;
        if (!show) inp.value = '';
        var fieldCol = inp.closest('.field');
        if (fieldCol) fieldCol.style.display = show ? '' : 'none';
      });

      if (row.classList.contains('naire-field--pair') && anyShow) {
        var pairKey = row.getAttribute('data-field-pair');
        var n = pairKey === '姓名1' ? 1 : pairKey === '姓名2' ? 2 : 3;
        var seiKey = n === 1 ? '姓①' : n === 2 ? '姓②' : '姓③';
        var meiKey = n === 1 ? '名①' : n === 2 ? '名②' : '名③';
        var titleChar = n === 1 ? '①' : n === 2 ? '②' : '③';
        var hasSei = fields.indexOf(seiKey) >= 0;
        var hasMei = fields.indexOf(meiKey) >= 0;
        var labelEl = row.querySelector('[data-naire-pair-heading]');
        if (labelEl) {
          if (hasSei && hasMei) labelEl.textContent = '姓名' + titleChar;
          else if (hasSei) labelEl.textContent = seiKey;
          else if (hasMei) labelEl.textContent = meiKey;
        }
      }
    });

    var cap = naireWrap.querySelector('[data-naire-caption]');
    if (cap) {
      var showCap = showPrintBlock && NAIRE_CAPTION_METHODS.indexOf(methodIdx) >= 0;
      cap.style.display = showCap ? '' : 'none';
      cap.hidden = !showCap;
    }
  }

  function initNoshiOptions() {
    var containers = document.querySelectorAll('[data-noshi-option]');
    containers.forEach(function (container) {
      var detailEl = container.querySelector('[data-noshi-detail]');
      var omotegakiEl = container.querySelector('[data-noshi-omotegaki]');
      var noshiRadios = container.querySelectorAll('input[name="properties[のし]"]');
      var typeRadios = container.querySelectorAll('input[name="properties[のし種類]"]');
      var styleRadios = container.querySelectorAll('input[name="properties[のしの付け方]"]');
      var naireRadios = container.querySelectorAll('input[name="properties[名入れ方法]"]');
      var purposeSelect = container.querySelector('select[name="properties[のし用途]"]');

      if (!detailEl) return;

      function updateNoshiDetail(shouldScroll) {
        var noshiYes = container.querySelector('input[name="properties[のし]"][value="のし必要"]');
        var isRequired = noshiYes && noshiYes.checked;
        detailEl.style.display = isRequired ? '' : 'none';
        if (!isRequired) {
          detailEl.querySelectorAll('input, select').forEach(function (el) {
            el.disabled = true;
          });
          updateNoshiStepVisibility(container);
          return;
        }
        updateNoshiTypeList(container);
        updateNoshiStepVisibility(container);
        if (shouldScroll) {
          scrollToNoshiNextStep(container, 'required');
        }
      }

      function syncNoshiSteps(nextStep) {
        var noshiYes = container.querySelector('input[name="properties[のし]"][value="のし必要"]');
        if (!noshiYes || !noshiYes.checked) return;
        updateNoshiTypeList(container);
        updateNoshiStepVisibility(container);
        if (nextStep) {
          scrollToNoshiNextStep(container, nextStep);
        }
      }

      noshiRadios.forEach(function (radio) {
        radio.addEventListener('change', function (e) {
          updateNoshiDetail(e.isTrusted);
        });
      });
      if (purposeSelect) {
        purposeSelect.addEventListener('change', function (e) {
          resetNoshiTypeSelection(container);
          if (purposeSelect.value && e.isTrusted) {
            syncNoshiSteps('purpose');
          } else {
            syncNoshiSteps();
          }
        });
      }
      typeRadios.forEach(function (radio) {
        radio.addEventListener('change', function (e) {
          syncNoshiSteps(e.isTrusted ? 'type' : null);
        });
      });
      styleRadios.forEach(function (radio) {
        radio.addEventListener('change', function (e) {
          syncNoshiSteps(e.isTrusted ? 'style' : null);
        });
      });
      naireRadios.forEach(function (radio) {
        radio.addEventListener('change', function (e) {
          var noshiYes = container.querySelector('input[name="properties[のし]"][value="のし必要"]');
          if (!noshiYes || !noshiYes.checked) return;
          updateNaireFields(container);

          var method = radio.dataset.method;
          if (e.isTrusted && method && method !== '0') {
            scrollToNoshiNextStep(container, 'naire-method');
          }
        });
      });

      updateNoshiDetail();
    });
  }

  // ---------------------------------------------------------------------------
  // 3. ギフトオプション（バリアント連動）：data-no-gift-option のときラッピング・のし一式を隠す
  //    のし「不要」に寄せ、入力の disabled を付け替え。変更後はのし用 UI を再同期
  // ---------------------------------------------------------------------------
  /** 現在のバリアントに合わせてギフト欄の表示・入力可否を更新 */
  function applyGiftOptionState() {
    var modal = document.querySelector('.option-modal');
    if (!modal) return;

    var giftAreas = modal.querySelectorAll('[data-gift-option-area]');
    if (!giftAreas.length) return;

    var variantSelects = modal.querySelector('variant-selects');
    if (!variantSelects) return;

    var shouldHide = false;
    var checkedRadio = variantSelects.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      var variantSku = String(checkedRadio.dataset.variantSku || '').toUpperCase();
      shouldHide =
        checkedRadio.dataset.noGiftOption === 'true' ||
        /-(SEAL|DATA)$/.test(variantSku);
    }

    if (shouldHide) {
      var noshiNo = modal.querySelector('input[name="properties[のし]"][value="のし不要"]');
      if (noshiNo) noshiNo.checked = true;
      var wrappingNo = modal.querySelector('input[name="properties[ラッピング]"][value="なし"]');
      if (wrappingNo) wrappingNo.checked = true;
    }

    giftAreas.forEach(function (area) {
      area.style.display = shouldHide ? 'none' : '';
    });

    var giftInputs = modal.querySelectorAll(
      '[data-gift-option-area="wrapping"] input, [data-gift-option-area="wrapping"] select, ' +
      '[data-gift-option-area="noshi"] input, [data-gift-option-area="noshi"] select'
    );
    giftInputs.forEach(function (input) {
      input.disabled = shouldHide;
    });

    if (!shouldHide) {
      var checkedNoshi = modal.querySelector('input[name="properties[のし]"]:checked');
      if (checkedNoshi) {
        checkedNoshi.dispatchEvent(new Event('change', { bubbles: true }));
      }
      var purposeSelectSync = modal.querySelector('select[name="properties[のし用途]"]');
      if (purposeSelectSync) {
        purposeSelectSync.dispatchEvent(new Event('change', { bubbles: true }));
      }
      var checkedNoshiType = modal.querySelector('input[name="properties[のし種類]"]:checked');
      if (checkedNoshiType) {
        checkedNoshiType.dispatchEvent(new Event('change', { bubbles: true }));
      }
      var checkedNaireMethod = modal.querySelector('input[name="properties[名入れ方法]"]:checked');
      if (checkedNaireMethod) {
        checkedNaireMethod.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    var sectionId = modal.dataset.sectionId || (modal.id ? modal.id.replace('OptionModal-', '') : '');
    if (sectionId) updateCartButtonState(sectionId);
  }

  /** バリアントのラジオ変更・PubSub の variantChange で applyGiftOptionState を走らせる */
  function initGiftOptionToggle() {
    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || target.type !== 'radio') return;
      if (!target.closest('variant-selects')) return;
      if (!target.closest('.option-modal')) return;
      applyGiftOptionState();
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        applyGiftOptionState();
      });
    }

    applyGiftOptionState();
  }

  // ---------------------------------------------------------------------------
  // 4. 下部固定カートバー（floating-cart-bar）とオプションモーダル（.option-modal）
  //    - PC/スマホでバー・モーダルの DOM 位置を調整
  //    - モーダル開閉・フッター送信と本フォームの数量・カートボタンを連携
  // ---------------------------------------------------------------------------
  var floatingCartBarInitialized = false;

  /** 画面幅に応じて固定バー／モーダルを product-info 内の適切な位置へ移動 */
  function positionFloatingBar() {
    var bar = document.querySelector('.floating-cart-bar');
    if (!bar) return;
    var productInfo = bar.closest('product-info');
    if (!productInfo) return;
    var sectionId = bar.dataset.sectionId;

    if (window.innerWidth > 750 && productInfo.dataset.scgCourses !== 'true') {
      if (sectionId) {
        var quantityForm = productInfo.querySelector('#Quantity-Form-' + sectionId);
        if (quantityForm && bar.previousElementSibling !== quantityForm) {
          quantityForm.insertAdjacentElement('afterend', bar);
        }
      }
      var pcModal = productInfo.querySelector('.option-modal');
      if (pcModal && pcModal.parentElement !== productInfo) {
        productInfo.appendChild(pcModal);
      }
      return;
    }

    if (bar.parentElement !== productInfo) {
      productInfo.appendChild(bar);
    }

    var modal = productInfo.querySelector('.option-modal');
    if (modal && modal.parentElement !== productInfo) {
      productInfo.appendChild(modal);
    }
  }

  /** セクションに対応するオプションモーダル要素を取得 */
  function getOptionModal(sectionId) {
    var productInfo = document.querySelector('product-info[data-section="' + sectionId + '"]') ||
      document.querySelector('product-info[id^="MainProduct-"]');
    if (!productInfo) return null;
    return productInfo.querySelector('#OptionModal-' + sectionId) || productInfo.querySelector('.option-modal');
  }

  /** モーダルを開き、数量を揃え、合計価格・カートボタン状態を更新 */
  function openOptionModal(sectionId) {
    var modal = getOptionModal(sectionId);
    if (!modal) return;

    var floatingQty = document.getElementById('Quantity-floating-' + sectionId);
    var modalQty = document.getElementById('Quantity-modal-' + sectionId);
    if (floatingQty && modalQty && modalQty.value !== floatingQty.value) {
      modalQty.value = floatingQty.value;
    }

    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    updateModalTotalPrice();
    updateCartButtonState(sectionId);
  }

  /** モーダルを閉じ、スクロールロック解除・カートボタン状態を更新 */
  function closeOptionModal(sectionId) {
    var modal = getOptionModal(sectionId);
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    updateCartButtonState(sectionId);
  }

  /** ラッピング／のし関連の必須項目が満たされているか（送信前チェック用） */
  function validateRequiredOptions(sectionId) {
    var productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    if (!productInfo) return true;
    var modal = getOptionModal(sectionId);
    if (!modal) return true;

    var variantSelects = modal.querySelector('variant-selects');
    var checkedVariant = variantSelects
      ? variantSelects.querySelector('input[type="radio"]:checked')
      : null;
    if (checkedVariant) {
      var selectedSku = String(checkedVariant.dataset.variantSku || '').toUpperCase();
      var skipsGiftOptions =
        checkedVariant.dataset.noGiftOption === 'true' ||
        /-(SEAL|DATA)$/.test(selectedSku);
      if (skipsGiftOptions) return true;
    }

    var designRadios = modal.querySelectorAll('input[name="properties[フォルダーデザイン]"]');
    if (designRadios.length > 0) {
      var designChecked = Array.prototype.some.call(designRadios, function (r) { return r.checked; });
      if (!designChecked) return false;
    }

    var wrappingRadios = modal.querySelectorAll('input[name="properties[ラッピング]"]');
    if (wrappingRadios.length > 0) {
      var wrappingChecked = Array.prototype.some.call(wrappingRadios, function (r) { return r.checked; });
      if (!wrappingChecked) return false;
    }

    var noshiRequired = modal.querySelector('[data-noshi-required]');
    if (noshiRequired) {
      var noshiYes = modal.querySelector('input[name="properties[のし]"][value="のし必要"]');
      var noshiNo = modal.querySelector('input[name="properties[のし]"][value="のし不要"]');
      if (!noshiYes || !noshiNo) return true;
      if (noshiNo.checked) return true;

      var purposeSelect = modal.querySelector('select[name="properties[のし用途]"]');
      if (purposeSelect && (!purposeSelect.value || purposeSelect.value === '')) return false;

      var typeChecked = modal.querySelector('input[name="properties[のし種類]"]:checked');
      if (!typeChecked) return false;

      var noshiStyleChecked = modal.querySelector('input[name="properties[のしの付け方]"]:checked');
      if (!noshiStyleChecked) return false;

      var naireMethodRadio = modal.querySelector('input[name="properties[名入れ方法]"]:checked');
      if (!naireMethodRadio) return false;

      var methodIdx =
        naireMethodRadio.dataset.method !== undefined ? parseInt(naireMethodRadio.dataset.method, 10) : 0;
      if (isNaN(methodIdx)) methodIdx = 0;

      if (methodIdx > 0) {
        var reqFields = NAIRE_METHOD_FIELDS[methodIdx] || [];
        for (var fi = 0; fi < reqFields.length; fi++) {
          var fn = reqFields[fi];
          var inp = modal.querySelector('[data-field="' + fn + '"]');
          if (!inp || !String(inp.value || '').trim()) return false;
        }
      }
    }

    return true;
  }

  /**
   * 下部固定バーの送信ボタン文言のみ切り替え（モーダル開閉に合わせる）。
   * スマホではモーダルフッターが見えにくいため固定バーが主操作になる想定。
   * data-label-option / data-label-add は needs_option_modal 時のみ Liquid で付与。
   */
  function updateFloatingSubmitLabel(sectionId) {
    var bar = document.getElementById('FloatingCartBar-' + sectionId);
    if (!bar) return;
    var btn = bar.querySelector('[data-floating-submit]');
    if (!btn || !btn.dataset.labelOption) return;
    var labelEl = btn.querySelector('[data-floating-submit-text]');
    if (!labelEl) return;

    var realSubmit = document.getElementById('ProductSubmitButton-' + sectionId);
    var soldOut = realSubmit && realSubmit.disabled;
    if (soldOut) return;

    var modal = getOptionModal(sectionId);
    var isOpen = modal && modal.getAttribute('aria-hidden') === 'false';
    labelEl.textContent = isOpen ? btn.dataset.labelAdd : btn.dataset.labelOption;
  }

  /** 固定バー側の疑似送信ボタン：モーダル開いているときは必須オプション未入力なら無効化 */
  function updateCartButtonState(sectionId) {
    var buttons = document.querySelectorAll('[data-floating-submit="' + sectionId + '"]');
    if (!buttons.length) return;
    var modal = getOptionModal(sectionId);
    var isOpen = modal && modal.getAttribute('aria-hidden') === 'false';
    var realSubmit = document.getElementById('ProductSubmitButton-' + sectionId);
    var soldOut = realSubmit && realSubmit.disabled;

    Array.prototype.forEach.call(buttons, function (btn) {
      if (!modal) {
        btn.classList.remove('floating-cart-bar__submit--disabled');
        btn.disabled = soldOut || false;
        return;
      }

      if (!isOpen) {
        btn.classList.remove('floating-cart-bar__submit--disabled');
        btn.disabled = soldOut || false;
        return;
      }

      var valid = validateRequiredOptions(sectionId);
      if (valid) {
        btn.classList.remove('floating-cart-bar__submit--disabled');
        btn.disabled = soldOut || false;
      } else {
        btn.classList.add('floating-cart-bar__submit--disabled');
        btn.disabled = true;
      }
    });

    updateFloatingSubmitLabel(sectionId);
  }

  /** クリックでモーダル開閉・本物のカートボタンへ委譲、数量3点同期、売り切れ監視 */
  function initFloatingCartBar() {
    if (floatingCartBarInitialized) return;
    floatingCartBarInitialized = true;

    positionFloatingBar();
    window.addEventListener('resize', positionFloatingBar);

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-option-modal-close]')) {
        var closeEl = e.target.closest('[data-option-modal-close]');
        var modal = closeEl.closest('.option-modal');
        if (modal) {
          var sectionId = modal.dataset.sectionId || modal.id.replace('OptionModal-', '');
          closeOptionModal(sectionId);
        }
        return;
      }

      var btn = e.target.closest('[data-floating-submit]');
      if (!btn) return;
      var sectionId = btn.dataset.floatingSubmit;
      var modal = getOptionModal(sectionId);

      if (modal) {
        var isOpen = modal.getAttribute('aria-hidden') === 'false';
        if (!isOpen) {
          e.preventDefault();
          openOptionModal(sectionId);
          return;
        }
        if (!validateRequiredOptions(sectionId)) return;
      }

      var mainQty = document.getElementById('Quantity-' + sectionId);
      var floatingQty = document.getElementById('Quantity-floating-' + sectionId);
      var modalQty = document.getElementById('Quantity-modal-' + sectionId);
      var sourceQty = floatingQty;
      if (modal && modal.getAttribute('aria-hidden') === 'false' && modalQty) {
        sourceQty = modalQty;
      }

      if (mainQty && sourceQty && mainQty.value !== sourceQty.value) {
        mainQty.value = sourceQty.value;
        mainQty.dispatchEvent(new Event('change', { bubbles: true }));
      }
      var realSubmit = document.getElementById('ProductSubmitButton-' + sectionId);
      if (realSubmit) realSubmit.click();

      if (modal) closeOptionModal(sectionId);
    });

    document.addEventListener('change', function (e) {
      var target = e.target;
      if (target && target.closest('.option-modal')) {
        var modal = target.closest('.option-modal');
        var sectionId = modal.dataset.sectionId || (modal.id ? modal.id.replace('OptionModal-', '') : '');
        if (sectionId) updateCartButtonState(sectionId);
      }
    });

    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || target.type !== 'number' || target.name !== 'quantity') return;
      var sectionId = target.id
        ? target.id.replace('Quantity-floating-', '').replace('Quantity-modal-', '').replace('Quantity-', '')
        : '';
      if (!sectionId) return;
      var mainInput = document.getElementById('Quantity-' + sectionId);
      var floatingInput = document.getElementById('Quantity-floating-' + sectionId);
      var modalInput = document.getElementById('Quantity-modal-' + sectionId);
      if (!mainInput) return;

      if (target === mainInput) {
        if (floatingInput && floatingInput.value !== mainInput.value) floatingInput.value = mainInput.value;
        if (modalInput && modalInput.value !== mainInput.value) modalInput.value = mainInput.value;
      } else if (target === floatingInput || target === modalInput) {
        if (mainInput.value !== target.value) {
          mainInput.value = target.value;
          mainInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        var peerInput = target === floatingInput ? modalInput : floatingInput;
        if (peerInput && peerInput.value !== target.value) {
          peerInput.value = target.value;
        }
      }
    });

    document.addEventListener('input', function (e) {
      var target = e.target;
      if (!target || target.type !== 'number' || target.name !== 'quantity') return;
      var sectionId = target.id
        ? target.id.replace('Quantity-floating-', '').replace('Quantity-modal-', '').replace('Quantity-', '')
        : '';
      var isFloatingInput = target.id && target.id.indexOf('Quantity-floating-') === 0;
      var isModalInput = target.id && target.id.indexOf('Quantity-modal-') === 0;
      var scope = target.closest('product-info');
      var isScgMainInput = scope && scope.dataset.scgCourses === 'true' && target.id === 'Quantity-' + sectionId;
      if (!sectionId || (!isFloatingInput && !isModalInput && !isScgMainInput)) return;
      var mainInput = document.getElementById('Quantity-' + sectionId);
      var floatingInput = document.getElementById('Quantity-floating-' + sectionId);
      var modalInput = document.getElementById('Quantity-modal-' + sectionId);
      if (!mainInput) return;
      if (mainInput.value !== target.value) {
        mainInput.value = target.value;
      }
      if (floatingInput && target !== floatingInput && floatingInput.value !== target.value) {
        floatingInput.value = target.value;
      }
      if (modalInput && target !== modalInput && modalInput.value !== target.value) {
        modalInput.value = target.value;
      }
    });

    var bar = document.querySelector('.floating-cart-bar');
    if (bar) {
      var barSectionId = bar.dataset.sectionId;
      if (barSectionId) {
        var realSubmitEl = document.getElementById('ProductSubmitButton-' + barSectionId);
        if (realSubmitEl) {
          new MutationObserver(function () {
            updateCartButtonState(barSectionId);
          }).observe(realSubmitEl, { attributes: true, attributeFilter: ['disabled'] });
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // 5. 価格表示：本体 #price-* の変化を固定バー／モーダルに同期し、オプション付き商品は
  //    ProductVariantsPricing JSON に基づき販売価／比較価を再計算（割引バッジ含む）
  // ---------------------------------------------------------------------------
  function formatMoney(cents) {
    var amount = Math.round(cents / 100);
    return '¥' + amount.toLocaleString('ja-JP');
  }
  var suppressNextPriceMutationSync = false;

  /**
   * main-product.liquid の ProductVariantsPricing JSON を取得
   */
  function getProductPricingConfig(sectionId) {
    var el = document.getElementById('ProductVariantsPricing-' + sectionId);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * 各バリエーションの compare_at_price が複数種類の場合は Liquid の表示を尊重して上書きしない
   */
  function compareAtPricesAreConsistent(variants) {
    if (!variants || !variants.length) return true;
    var distinct = {};
    for (var i = 0; i < variants.length; i++) {
      var c = variants[i].compare_at_price || 0;
      if (c > 0) distinct[c] = true;
    }
    var keys = Object.keys(distinct);
    return keys.length <= 1;
  }

  /**
   * 選択中バリエーション（variant-selects の JSON、またはデフォルト1種のフォームから）
   */
  function getSelectedVariantForPricing(sectionId) {
    var productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    if (!productInfo) return null;
    var variantJson = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (variantJson) {
      try {
        var v = JSON.parse(variantJson.textContent);
        return v && v.price != null ? v : null;
      } catch (e) {
        return null;
      }
    }
    var form = document.getElementById('product-form-' + sectionId);
    if (!form) return null;
    var idInput = form.querySelector('input[name="id"]');
    if (!idInput || !idInput.value) return null;
    var vid = parseInt(idInput.value, 10);
    var config = getProductPricingConfig(sectionId);
    if (!config || !config.variants) return null;
    for (var j = 0; j < config.variants.length; j++) {
      if (config.variants[j].id === vid) {
        return { id: vid, price: config.variants[j].price, compare_at_price: config.variants[j].compare_at_price };
      }
    }
    return null;
  }

  function getVariantPrice() {
    var productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    if (!productInfo || !productInfo.dataset.section) return null;
    var v = getSelectedVariantForPricing(productInfo.dataset.section);
    return v ? v.price : null;
  }

  function setCustomSaleLine(container, centsTotal) {
    if (!container) return;
    var saleEl = container.querySelector('.price-item--sale.price-item--last');
    if (!saleEl) return;
    var hadTaxLabel = !!saleEl.querySelector('.price-tax-label');
    saleEl.textContent = formatMoney(centsTotal);
    if (hadTaxLabel) {
      var taxSpan = document.createElement('span');
      taxSpan.className = 'price-tax-label';
      taxSpan.textContent = '税込';
      saleEl.appendChild(taxSpan);
    }
  }

  function setCustomCompareLine(container, centsTotal) {
    if (!container) return;
    var sEl = container.querySelector('.price-item--regular.price-item--compare s');
    if (!sEl) return;
    sEl.textContent = '定価' + formatMoney(centsTotal);
  }

  function toggleOnSaleClass(container, saleCents, compareCents) {
    var priceRoot = container.querySelector('.price');
    if (!priceRoot) return;
    var isOnSale = compareCents > 0 && compareCents > saleCents;
    priceRoot.classList.toggle('price--on-sale', isOnSale);
  }

  /**
   * 割引率バッジは「表示中の定価と販売価格」から再計算する
   * 必要ならバッジを動的に生成し、セールでない場合は非表示にする
   */
  function updateDiscountBadgeIfPresent(container, saleCents, compareCents) {
    var saleRow = container.querySelector('.price-item--sale-row');
    var badge = container.querySelector('.price__badge-discount');
    var isOnSale = compareCents > 0 && compareCents > saleCents;

    if (!isOnSale) {
      if (badge) badge.remove();
      return;
    }

    if (!badge && saleRow) {
      badge = document.createElement('span');
      badge.className = 'badge price__badge-sale price__badge-discount';
      var freeShippingBadge = saleRow.querySelector('.price__badge-free-shipping');
      if (freeShippingBadge) saleRow.insertBefore(badge, freeShippingBadge);
      else saleRow.appendChild(badge);
    }

    if (!badge) return;

    var rate = Math.round(((compareCents - saleCents) / compareCents) * 100);
    badge.textContent = rate + '%OFF';
  }

  /**
   * 通常価格 = baseCompareAtPrice + (選択中販売価格 - 最安販売価格)
   * 商品詳細本体は単価、固定バー・モーダルは数量分の合計
   */
  function applyLinkedVariantPricing(sectionId) {
    var config = getProductPricingConfig(sectionId);
    var variant = getSelectedVariantForPricing(sectionId);
    if (!variant || !config) return false;

    var baseCompare = config.baseCompareAtPrice || 0;
    var minPrice = config.minVariantPrice;
    if (!config.useVariantCompareAtPrice) {
      if (minPrice == null || minPrice === '' || baseCompare <= 0) return false;
      if (config.variants && !compareAtPricesAreConsistent(config.variants)) return false;
    }

    var unitSale = variant.price;
    var unitCompare = config.useVariantCompareAtPrice
      ? (variant.compare_at_price || 0)
      : baseCompare + (unitSale - minPrice);

    var modalQty = document.getElementById('Quantity-modal-' + sectionId);
    var floatingQty = document.getElementById('Quantity-floating-' + sectionId);
    var qtyBar = 1;
    if (modalQty) qtyBar = parseInt(modalQty.value, 10) || 1;
    else if (floatingQty) qtyBar = parseInt(floatingQty.value, 10) || 1;

    var mainPriceEl = document.getElementById('price-' + sectionId);
    var floatingPrice = document.querySelector('.floating-cart-bar__price');
    var modalPrice = document.querySelector('.option-modal__actions-price');

    if (mainPriceEl) {
      suppressNextPriceMutationSync = true;
      setCustomSaleLine(mainPriceEl, unitSale);
      setCustomCompareLine(mainPriceEl, unitCompare);
      toggleOnSaleClass(mainPriceEl, unitSale, unitCompare);
      updateDiscountBadgeIfPresent(mainPriceEl, unitSale, unitCompare);
    }
    if (floatingPrice) {
      setCustomSaleLine(floatingPrice, unitSale * qtyBar);
      setCustomCompareLine(floatingPrice, unitCompare * qtyBar);
      toggleOnSaleClass(floatingPrice, unitSale * qtyBar, unitCompare * qtyBar);
      updateDiscountBadgeIfPresent(floatingPrice, unitSale * qtyBar, unitCompare * qtyBar);
    }
    if (modalPrice) {
      setCustomSaleLine(modalPrice, unitSale * qtyBar);
      setCustomCompareLine(modalPrice, unitCompare * qtyBar);
      toggleOnSaleClass(modalPrice, unitSale * qtyBar, unitCompare * qtyBar);
      updateDiscountBadgeIfPresent(modalPrice, unitSale * qtyBar, unitCompare * qtyBar);
    }

    return true;
  }

  function updatePriceWithTotal(container, total) {
    if (!container) return;
    var saleEl = container.querySelector('.price-item--sale.price-item--last');
    var regularEl = !saleEl ? container.querySelector('.price__regular .price-item--regular') : null;
    var targetEl = saleEl || regularEl;
    if (!targetEl) return;

    var hadTaxLabel = !!targetEl.querySelector('.price-tax-label');
    targetEl.textContent = formatMoney(total);
    if (hadTaxLabel) {
      var taxSpan = document.createElement('span');
      taxSpan.className = 'price-tax-label';
      taxSpan.textContent = '税込';
      targetEl.appendChild(taxSpan);
    }
  }

  function updateModalTotalPrice() {
    var productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    if (!productInfo) return;
    var sectionId = productInfo.dataset.section;
    if (!sectionId) return;

    if (applyLinkedVariantPricing(sectionId)) return;

    var unitPrice = getVariantPrice();
    if (!unitPrice) return;

    var modalQty = document.getElementById('Quantity-modal-' + sectionId);
    var floatingQty = document.getElementById('Quantity-floating-' + sectionId);
    var qty = 1;
    if (modalQty) qty = parseInt(modalQty.value, 10) || 1;
    else if (floatingQty) qty = parseInt(floatingQty.value, 10) || 1;
    var total = unitPrice * qty;

    updatePriceWithTotal(document.querySelector('.option-modal__actions-price'), total);
    updatePriceWithTotal(document.querySelector('.floating-cart-bar__price'), total);
  }

  /**
   * 本体の価格ブロックを MutationObserver で監視し、固定バー／モーダルへ HTML をコピー同期。
   * 数量変更時は合計（updateModalTotalPrice）を更新。
   */
  function initPriceSync() {
    var productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    if (!productInfo) return;
    var sectionId = productInfo.dataset.section;
    if (!sectionId) return;
    var mainPriceEl = document.getElementById('price-' + sectionId);
    if (!mainPriceEl) return;

    function syncPrices() {
      if (suppressNextPriceMutationSync) {
        suppressNextPriceMutationSync = false;
        return;
      }

      var priceCopy = mainPriceEl.cloneNode(true);
      priceCopy.querySelectorAll('[data-scg-course-summary]').forEach(function (node) { node.remove(); });
      var html = priceCopy.innerHTML;
      var floatingHtml = html.replace(/price--large/g, 'price--floating');

      var floatingPrice = document.querySelector('.floating-cart-bar__price');
      if (floatingPrice) floatingPrice.innerHTML = floatingHtml;

      var modalPrice = document.querySelector('.option-modal__actions-price');
      if (modalPrice) modalPrice.innerHTML = floatingHtml;

      updateModalTotalPrice();
    }

    var observer = new MutationObserver(syncPrices);
    observer.observe(mainPriceEl, { childList: true, subtree: true, characterData: true });

    var qtyModalId = 'Quantity-modal-' + sectionId;
    var qtyFloatingId = 'Quantity-floating-' + sectionId;
    function isQtyInput(e) {
      return e.target.id === qtyModalId || e.target.id === qtyFloatingId ||
        (productInfo.dataset.scgCourses === 'true' && e.target.id === 'Quantity-' + sectionId);
    }
    document.addEventListener('change', function (e) {
      if (isQtyInput(e)) updateModalTotalPrice();
    });
    document.addEventListener('input', function (e) {
      if (isQtyInput(e)) updateModalTotalPrice();
    });

    requestAnimationFrame(function () {
      syncPrices();
    });
  }

  // ---------------------------------------------------------------------------
  // 初期化：DOM 準備後に各機能をまとめて起動
  // ---------------------------------------------------------------------------
  function init() {
    enableLoopForGallerySliders();
    initNoshiOptions();
    initGiftOptionToggle();
    initFloatingCartBar();
    initPriceSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      requestAnimationFrame(init);
    });
  } else {
    requestAnimationFrame(init);
  }
})();

if (!customElements.get('scg-course-bar')) {
  customElements.define('scg-course-bar', class extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest('product-info');
      if (!this.scope) return;
      this.courses = JSON.parse(this.querySelector('[data-scg-course-data]').textContent);
      this.onClick = (event) => {
        const button = event.target.closest('[data-scg-course-sku]');
        if (!button || button.getAttribute('aria-pressed') === 'true') return;
        const input = Array.from(this.scope.querySelectorAll('variant-selects input[data-variant-sku]'))
          .find((node) => node.dataset.variantSku === button.dataset.scgCourseSku);
        if (!input) return;
        this.scope.dataset.scgKeepViewport = 'true';
        this.setAttribute('aria-busy', 'true');
        input.click();
      };
      this.onFailure = () => {
        delete this.scope.dataset.scgKeepViewport;
        this.setAttribute('aria-busy', 'false');
        this.syncSelected();
      };
      this.addEventListener('click', this.onClick);
      this.scope.addEventListener('scg:variant-error', this.onFailure);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) !== this.dataset.section) return;
        this.sync(data.variant);
        delete this.scope.dataset.scgKeepViewport;
        this.setAttribute('aria-busy', 'false');
      });
      this.resizeObserver = new ResizeObserver(() => {
        const bar = this.closest('.floating-cart-bar');
        if (bar) document.documentElement.style.setProperty('--scg-course-bar-height', bar.getBoundingClientRect().height + 'px');
      });
      this.resizeObserver.observe(this.closest('.floating-cart-bar'));
      this.stickyBar = this.closest('.floating-cart-bar');
      this.updateStickyVisibility = () => {
        this.stickyFrame = null;
        const button = this.scope.querySelector('.scg-inline-submit');
        const visible = !!button && button.getBoundingClientRect().bottom <= 0;
        this.stickyBar.classList.toggle('scg-bar-visible', visible);
        this.stickyBar.inert = !visible;
        this.stickyBar.setAttribute('aria-hidden', String(!visible));
      };
      this.onStickyScroll = () => {
        if (this.stickyFrame == null) this.stickyFrame = requestAnimationFrame(this.updateStickyVisibility);
      };
      window.addEventListener('scroll', this.onStickyScroll, { passive: true });
      window.addEventListener('resize', this.onStickyScroll);
      this.positionObserver = new ResizeObserver(this.onStickyScroll);
      this.positionObserver.observe(this.scope);
      this.updateStickyVisibility();
      this.syncSelected();
      this.onSectionLoad = () => this.syncSelected();
      document.addEventListener('shopify:section:load', this.onSectionLoad);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.scope?.removeEventListener('scg:variant-error', this.onFailure);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      this.unsubscribe?.();
      this.resizeObserver?.disconnect();
      this.positionObserver?.disconnect();
      window.removeEventListener('scroll', this.onStickyScroll);
      window.removeEventListener('resize', this.onStickyScroll);
      if (this.stickyFrame != null) cancelAnimationFrame(this.stickyFrame);
    }

    syncSelected() {
      const selected = this.scope.querySelector('variant-selects [data-selected-variant]');
      if (selected) this.sync(JSON.parse(selected.textContent));
    }

    sync(variant) {
      const course = this.courses.find((entry) => String(entry.id) === String(variant?.id));
      if (!course) return;
      this.querySelectorAll('[data-scg-course-sku]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.scgCourseSku === course.sku));
      });
      const mainStrip = this.scope.querySelector('.scg-course-options');
      if (mainStrip && mainStrip.scrollWidth > mainStrip.clientWidth) {
        const selectedInput = Array.from(mainStrip.querySelectorAll('input[data-variant-sku]')).find((input) => input.dataset.variantSku === course.sku);
        const selectedLabel = selectedInput?.nextElementSibling;
        if (selectedLabel) {
          const viewport = mainStrip.getBoundingClientRect();
          const item = selectedLabel.getBoundingClientRect();
          if (item.left < viewport.left || item.right > viewport.right) {
            mainStrip.scrollTo({ left: mainStrip.scrollLeft + item.left - viewport.left - 3, behavior: 'auto' });
          }
        }
      }
      const strip = this.querySelector('.scg-footer-courses');
      const active = this.querySelector('[aria-pressed="true"]');
      if (strip && active) {
        const parentRect = strip.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        if (activeRect.left < parentRect.left || activeRect.right > parentRect.right) {
          strip.scrollTo({ left: strip.scrollLeft + activeRect.left - parentRect.left - 8, behavior: 'auto' });
        }
      }
      document.querySelectorAll('[data-scg-specs]').forEach((section) => {
        if (section.dataset.scgSpecs !== this.dataset.product) return;
        section.querySelectorAll('[data-scg-spec-field]').forEach((node) => {
          const key = node.dataset.scgSpecField;
          if (key === 'html' || key === 'points') node.innerHTML = course[key] || '';
          else node.textContent = course[key] || '';
        });
      });
      document.querySelectorAll('[data-scg-demo]').forEach((section) => {
        if (section.dataset.scgDemo !== this.dataset.product) return;
        const link = section.querySelector('.demo-site-banner__button');
        if (link && course.demoUrl) link.href = course.demoUrl;
        [['DemoSiteId-', 'demoId'], ['DemoSitePass-', 'demoPass']].forEach(([prefix, key]) => {
          const value = section.querySelector('[id^="' + prefix + '"]');
          if (!value) return;
          value.textContent = course[key] || '';
          section.querySelectorAll('[data-copy-target]').forEach((button) => {
            if (button.dataset.copyTarget === value.id) button.dataset.copyText = course[key] || '';
          });
        });
      });
    }
  });
}

